import React, { useState } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as ChevronDown } from '@/assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from '@/assets/icons/chevron-up.svg';

import styles from './SelectInput.module.css';
import Input from '../Input/Input';
import useIsMobileView from '@/hooks/useIsMobileView';

interface SelectInputProps<T> {
  options: T[];
  value: T | undefined;
  onChange: (value: T) => void;
  displayValue: (option: T) => string;
  renderOption?: (option: T) => React.ReactNode;
  className?: string;
  inputClassName?: string;
  optionsClassName?: string;
  optionClassName?: string;
}

const SelectInput = <T,>({
  options,
  value,
  onChange,
  displayValue,
  renderOption,
  inputClassName = '',
  optionsClassName = '',
  optionClassName = '',
}: SelectInputProps<T>) => {
  const isMobileView = useIsMobileView();
  const [query, setQuery] = useState('');

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) => {
          return displayValue(option).toLowerCase().includes(query.toLowerCase());
        });
  console.log('filteredOptions', filteredOptions);
  return (
    <div className="relative">
      <Combobox value={value} onChange={onChange} onClose={() => setQuery('')}>
        <div className="group/select relative">
          <ComboboxInput
            as={Input}
            displayValue={displayValue}
            onChange={(event) => setQuery(event.target.value)}
            className={twMerge(styles.selectInput, inputClassName)}
            fullWidth
          ></ComboboxInput>
          <ComboboxButton className="group absolute inset-y-0 right-0 flex items-center pr-5">
            <ChevronDown className="block text-ph-white-30 group-hover/select:text-white group-data-[open]:hidden" />
            <ChevronUp className="hidden text-ph-white-30 group-hover/select:text-white group-data-[open]:block" />
          </ComboboxButton>
          <ComboboxOptions
            // anchor="bottom"
            className={twMerge(
              'scrollbar-phenom absolute z-[11] mt-1 w-full overflow-y-auto rounded-b-lg bg-ph-gray-field py-3 text-sm font-normal text-white',
              isMobileView ? 'max-h-[50dvh]' : 'max-h-60',
              'w-[var(--input-width)] [--anchor-gap:var(--spacing-1)] empty:invisible',
              'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
              styles.selectOptions,
              optionsClassName,
            )}
          >
            {filteredOptions.map((option, index) => (
              <ComboboxOption
                key={index}
                value={option}
                className={twMerge(
                  styles.selectOption,
                  'relative flex cursor-default select-none items-center gap-2 bg-ph-gray-field py-2 pl-4 text-sm font-normal text-white hover:cursor-pointer hover:bg-ph-gray-evening',
                  optionClassName,
                )}
              >
                {renderOption ? renderOption(option) : displayValue(option)}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
};

export default SelectInput;
